/**
 * Presentation rules of a table that are not typed values: the fixed background palette (the
 * server accepts only these colors), the deterministic precedence cell > row > column > theme,
 * the translucent mix that keeps text readable in both themes, the estimated height of wrapped
 * rows and cell ranges. Pure and unit-tested.
 */
import { DEFAULT_SIZE, headerStyleOf, lineHeightOf, safeStyle } from './style';
import type { DatabaseColumn, DatabaseRecord, FieldOption, FieldType } from './types';

export const FORMAT_COLORS = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#78716c',
] as const;

export const FORMAT_COLOR_NAMES = [
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'violet',
  'pink',
  'gray',
] as const;

const HEX = /^#[0-9a-f]{6}$/i;
export const isFormatColor = (value: unknown): value is string =>
  typeof value === 'string' && (FORMAT_COLORS as readonly string[]).includes(value.toLowerCase());

/**
 * The color a cell is drawn with, per layer: the explicit CELL color, else the row's, else the
 * column's - each a palette color or any `#rrggbb` (a palette color is drawn as a 30% tint, a
 * custom one exactly as chosen: `backgroundOf`).
 */
export function cellColor(
  column: Pick<DatabaseColumn, 'id' | 'color'>,
  record: Pick<DatabaseRecord, 'color' | 'cell_colors'>,
): string | null {
  const own = record.cell_colors[column.id];
  if (typeof own === 'string' && HEX.test(own)) return own.toLowerCase();
  for (const color of [record.color, column.color]) {
    if (typeof color === 'string' && HEX.test(color)) return color.toLowerCase();
  }
  return null;
}

/** CSS background of a formatting color: a translucent layer over the theme surface, so it reads
 * well on light and dark (a solid pastel would glare in the dark theme). */
export const backgroundOf = (color: string): string =>
  isFormatColor(color) ? `color-mix(in srgb, ${color} 30%, var(--color-surface))` : color;

/**
 * The see-through counterpart of `backgroundOf` (pinned columns set to "transparent"): the same
 * hue and strength, mixed with nothing instead of the page, so what scrolls behind shows through.
 * A custom fill keeps most of its strength.
 */
export const translucentOf = (color: string): string =>
  `color-mix(in srgb, ${color} ${isFormatColor(color) ? '30' : '75'}%, transparent)`;

// --- option colors ---------------------------------------------------------------------------

/** The configured color of an option, or `null` for a missing / malformed one (neutral chip). */
export function optionColor(option: Pick<FieldOption, 'color'> | undefined): string | null {
  const color = option?.color;
  return typeof color === 'string' && HEX.test(color) ? color : null;
}

// --- wrapped rows ----------------------------------------------------------------------------

/** Whether a cell wraps: its own choice, else the column's default. */
export const isWrapped = (
  column: Pick<DatabaseColumn, 'id' | 'wrap_text'>,
  record: Pick<DatabaseRecord, 'cell_wrap'>,
): boolean => record.cell_wrap[column.id] ?? column.wrap_text;

export const ROW_HEIGHT = 36;
const ROW_PADDING = 16;
/**
 * The most a row grows ON ITS OWN, however large the text: one extreme value must not turn the
 * table into a wall of text. What does not fit is marked (`isClipped`), is read in full in the
 * cell preview and can be given room by hand (`MAX_MANUAL_ROW_HEIGHT`).
 */
export const MAX_ROW_HEIGHT = 480;
/** The tallest a row can be made by hand (resize or fit); the smallest is `ROW_HEIGHT`. */
export const MAX_MANUAL_ROW_HEIGHT = 1200;
const CHAR_WIDTH = 7.4;

/** Lines `text` needs in a cell `width` px wide at `size` px (estimated: hard breaks and soft
 * wrapping; a larger size fits fewer characters on a line). */
export function wrappedLines(text: string, width: number, size: number = DEFAULT_SIZE): number {
  const perLine = Math.max(4, Math.floor((width - 20) / (CHAR_WIDTH * (size / DEFAULT_SIZE))));
  return text
    .split('\n')
    .reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / perLine)), 0);
}

type RowColumn = Pick<DatabaseColumn, 'id' | 'wrap_text'> & { field_type?: FieldType };
type RowRecord = Pick<DatabaseRecord, 'cells' | 'cell_wrap'> &
  Partial<Pick<DatabaseRecord, 'cell_styles'>>;

/** A stored / typed row height that can be applied: a finite number inside the supported range
 * (rounded), else `null` ("automatic"). */
export function validRowHeight(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (value < ROW_HEIGHT || value > MAX_MANUAL_ROW_HEIGHT) return null;
  return Math.round(value);
}

/** The height the content of ONE cell needs (not capped). */
export function cellContentHeight(
  column: RowColumn,
  record: RowRecord,
  widthOf: (columnId: string) => number,
): number {
  const size = safeStyle(record.cell_styles?.[column.id])?.size;
  const line = lineHeightOf(size);
  const chosen = record.cells[column.id];
  let lines = 1;
  if (column.field_type === 'multi_select') {
    // Multiple choices are listed one per line, wrapped or not.
    if (Array.isArray(chosen)) lines = Math.max(1, chosen.length);
  } else if (isWrapped(column, record) && typeof chosen === 'string') {
    lines = wrappedLines(chosen, widthOf(column.id), size);
  }
  return lines * line + ROW_PADDING;
}

/** What the tallest cell of the row needs: the height that shows everything (not capped). */
export function fitRowHeight(
  columns: readonly RowColumn[],
  record: RowRecord,
  widthOf: (columnId: string) => number,
): number {
  let height = ROW_HEIGHT;
  for (const column of columns) {
    height = Math.max(height, cellContentHeight(column, record, widthOf));
  }
  return Math.min(Math.ceil(height), MAX_MANUAL_ROW_HEIGHT);
}

/**
 * Height of a row. A height chosen by hand (`manual`, validated) wins. Otherwise: the default
 * unless a cell needs more - lines of wrapped text, one line per selected multiple choice, or a
 * larger text size (UD-UX3) - each at ITS text size; the tallest cell decides, up to
 * `MAX_ROW_HEIGHT` (never drawn over a neighbor: the rest is in the cell preview).
 */
export function rowHeight(
  columns: readonly RowColumn[],
  record: RowRecord,
  widthOf: (columnId: string) => number,
  manual?: number | null,
): number {
  const chosen = validRowHeight(manual);
  if (chosen !== null) return chosen;
  return Math.min(fitRowHeight(columns, record, widthOf), MAX_ROW_HEIGHT);
}

// --- column headers ----------------------------------------------------------------------------

const HEADER_PADDING = 16;
const MIN_HEADER_HEIGHT = 38;
const MAX_HEADER_HEIGHT = 160;
/** Width of what shares the header with the name: field icon, menu button, gaps, cell padding. */
const HEADER_CHROME = 68;
/** Extra width of marks that appear next to the name (sort, filter, frozen, drag grip). */
const HEADER_MARK = 16;
/** The header name is semi-bold: it fits slightly fewer characters than a cell of the same size. */
const HEADER_WEIGHT_FACTOR = 1.1;
const MAX_HEADER_LINES = 6;

/** How many lines a header name may take at `size` (what fits `MAX_HEADER_HEIGHT`, at most four). */
export function headerLineLimit(size?: number): number {
  return Math.max(
    1,
    Math.min(
      MAX_HEADER_LINES,
      Math.floor((MAX_HEADER_HEIGHT - HEADER_PADDING) / lineHeightOf(size)),
    ),
  );
}

/**
 * Height of the header row. A header name is ONE line (cut with an ellipsis) unless the column's
 * header style says `wrap` - an explicit choice, separate from wrapping the column's CELLS. Only
 * such headers add lines: the row is as tall as the tallest of them needs at its own size and its
 * column's width. Estimated like the rows (`wrappedLines`) and capped; what does not fit is in the
 * tooltip (the full name).
 */
export function headerRowHeight(
  columns: readonly Pick<DatabaseColumn, 'id' | 'name' | 'header_style'>[],
  widthOf?: (columnId: string) => number,
  marks?: (columnId: string) => number,
): number {
  let height = MIN_HEADER_HEIGHT;
  for (const column of columns) {
    const size = headerStyleOf(column)?.size;
    const line = lineHeightOf(size);
    let lines = 1;
    if (widthOf && headerStyleOf(column)?.wrap === true) {
      const room =
        widthOf(column.id) - HEADER_CHROME - HEADER_MARK * (marks ? marks(column.id) : 0);
      lines = Math.min(
        wrappedLines(column.name, room + 20, (size ?? DEFAULT_SIZE) * HEADER_WEIGHT_FACTOR),
        headerLineLimit(size),
      );
    }
    height = Math.max(height, lines * line + HEADER_PADDING);
  }
  return Math.min(height, MAX_HEADER_HEIGHT);
}

// --- cell ranges -----------------------------------------------------------------------------

/** A rectangle of cells by row and column index (inclusive, normalized). */
export interface CellRange {
  row1: number;
  row2: number;
  col1: number;
  col2: number;
}

export function rangeBetween(a: { row: number; col: number }, b: { row: number; col: number }) {
  return {
    row1: Math.min(a.row, b.row),
    row2: Math.max(a.row, b.row),
    col1: Math.min(a.col, b.col),
    col2: Math.max(a.col, b.col),
  } satisfies CellRange;
}

export const inRange = (range: CellRange | null, row: number, col: number): boolean =>
  range !== null &&
  row >= range.row1 &&
  row <= range.row2 &&
  col >= range.col1 &&
  col <= range.col2;

/** How many frozen columns may be pinned at this grid width: never more than half of it. */
export function usableFrozen(widths: readonly number[], containerWidth: number): number {
  let total = 0;
  let count = 0;
  for (const width of widths) {
    total += width;
    if (total > containerWidth / 2) break;
    count += 1;
  }
  return count;
}
